"use client";

import { useState, useEffect, useCallback } from "react";

interface Props {
  token: string;
}

interface OdooStatus {
  connected: boolean;
  uid?: number;
  version?: Record<string, unknown>;
  error?: string;
}

interface Invoice {
  id?: number;
  name: string;
  partner_id?: [number, string];
  invoice_date: string;
  invoice_date_due?: string;
  amount_total: number;
  amount_residual: number;
  payment_state: string;
  state: string;
}

interface SyncResult {
  username: string;
  status: "synced" | "error";
  odoo_partner_id?: number;
  error?: string;
}

type OdooTab = "status" | "invoices" | "sync";

const paymentBadge = (state: string) => {
  const map: Record<string, string> = {
    paid: "bg-green-100 text-green-700",
    not_paid: "bg-yellow-100 text-yellow-800",
    partial: "bg-orange-100 text-orange-700",
    in_payment: "bg-blue-100 text-blue-700",
    reversed: "bg-gray-100 text-gray-500",
  };
  const labels: Record<string, string> = {
    paid: "Betalt",
    not_paid: "Ubetalt",
    partial: "Delvist",
    in_payment: "Under behandling",
    reversed: "Krediteret",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[state] ?? "bg-gray-100 text-gray-500"}`}>
      {labels[state] ?? state}
    </span>
  );
};

export default function OdooAdmin({ token }: Props) {
  const [tab, setTab] = useState<OdooTab>("status");

  // Status
  const [status, setStatus] = useState<OdooStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [databases, setDatabases] = useState<string[]>([]);
  const [dbLoading, setDbLoading] = useState(false);

  // Invoices
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceOffset, setInvoiceOffset] = useState(0);
  const [invoiceLimit] = useState(50);
  const [invoiceFilter, setInvoiceFilter] = useState<"all" | "not_paid" | "paid" | "draft">("all");

  // Sync
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResult[] | null>(null);
  const [syncMsg, setSyncMsg] = useState("");

  const authH = useCallback(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const checkStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await fetch("/api/odoo/status", { headers: authH() });
      if (res.ok) setStatus(await res.json());
      else setStatus({ connected: false, error: `HTTP ${res.status}` });
    } catch (e) {
      setStatus({ connected: false, error: String(e) });
    } finally {
      setStatusLoading(false);
    }
  }, [authH]);

  const loadDatabases = useCallback(async () => {
    setDbLoading(true);
    try {
      const res = await fetch("/api/odoo/databases", { headers: authH() });
      if (res.ok) { const d = await res.json(); setDatabases(d.databases ?? []); }
    } finally {
      setDbLoading(false);
    }
  }, [authH]);

  const loadInvoices = useCallback(async () => {
    setInvoicesLoading(true);
    try {
      const res = await fetch(`/api/odoo/invoices?limit=${invoiceLimit}&offset=${invoiceOffset}`, { headers: authH() });
      if (res.ok) { const d = await res.json(); setInvoices(d.invoices ?? []); }
    } finally {
      setInvoicesLoading(false);
    }
  }, [authH, invoiceLimit, invoiceOffset]);

  useEffect(() => {
    if (tab === "status") { checkStatus(); loadDatabases(); }
    if (tab === "invoices") loadInvoices();
  }, [tab, checkStatus, loadDatabases, loadInvoices]);

  const syncAll = async () => {
    setSyncing(true);
    setSyncMsg("");
    setSyncResults(null);
    try {
      const res = await fetch("/api/odoo/sync-all", { method: "POST", headers: authH() });
      if (res.ok) {
        const data = await res.json();
        setSyncMsg(data.message);
        setSyncResults(data.results ?? []);
      } else {
        const e = await res.json().catch(() => ({}));
        setSyncMsg("❌ " + (e.detail ?? "Fejl"));
      }
    } finally {
      setSyncing(false);
    }
  };

  const filteredInvoices = invoiceFilter === "all"
    ? invoices
    : invoices.filter((inv) => {
        if (invoiceFilter === "draft") return inv.state === "draft";
        return inv.payment_state === invoiceFilter;
      });

  const totalOutstanding = invoices
    .filter((i) => i.payment_state === "not_paid" || i.payment_state === "partial")
    .reduce((s, i) => s + (i.amount_residual ?? 0), 0);

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {([
          { id: "status" as const, label: "🔌 Forbindelse" },
          { id: "invoices" as const, label: "🧾 Fakturaer" },
          { id: "sync" as const, label: "🔄 Synkronisering" },
        ]).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Status tab ── */}
      {tab === "status" && (
        <div className="space-y-4">
          {/* Connection card */}
          <div className={`rounded-xl border p-5 ${
            status?.connected ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                {status?.connected ? "✅ Odoo forbundet" : "❌ Ikke forbundet"}
                {statusLoading && <span className="text-xs text-gray-400 animate-pulse">Tjekker...</span>}
              </h3>
              <button
                onClick={checkStatus}
                disabled={statusLoading}
                className="px-3 py-1.5 text-xs border border-gray-200 bg-white rounded-lg hover:bg-gray-50 disabled:opacity-40"
              >
                🔄 Gentest
              </button>
            </div>

            {status?.connected ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-xs text-gray-500 block">Odoo UID</span><strong>{status.uid}</strong></div>
                {status.version && (
                  <>
                    <div>
                      <span className="text-xs text-gray-500 block">Server version</span>
                      <strong>{(status.version as Record<string, unknown>).server_version as string ?? "–"}</strong>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 block">Protokol</span>
                      {(status.version as Record<string, unknown>).protocol_version as string ?? "–"}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-red-600">{status?.error ?? "Tjekker forbindelse..."}</p>
            )}
          </div>

          {/* Config card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Konfiguration</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                { label: "URL", value: "http://192.168.1.32:8069" },
                { label: "Database", value: "odoo" },
                { label: "Brugernavn", value: "api" },
                { label: "API", value: "XML-RPC 2.0" },
              ].map(({ label, value }) => (
                <div key={label}>
                  <span className="text-xs text-gray-500 block">{label}</span>
                  <span className="font-mono text-xs text-gray-700">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Databases */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Tilgængelige databaser</h3>
              <button
                onClick={loadDatabases}
                disabled={dbLoading}
                className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-40"
              >
                🔄
              </button>
            </div>
            {dbLoading ? (
              <p className="text-sm text-gray-400">Henter...</p>
            ) : databases.length === 0 ? (
              <p className="text-sm text-gray-400">Ingen databaser fundet</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {databases.map((db) => (
                  <span
                    key={db}
                    className={`px-3 py-1 rounded-full text-sm ${db === "odoo" ? "bg-blue-100 text-blue-700 font-medium" : "bg-gray-100 text-gray-600"}`}
                  >
                    {db}{db === "odoo" && " ✓"}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Invoices tab ── */}
      {tab === "invoices" && (
        <div className="space-y-4">
          {/* Summary row */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Alle fakturaer", value: invoices.length, color: "text-gray-900" },
              { label: "Udestående", value: invoices.filter((i) => i.payment_state === "not_paid" || i.payment_state === "partial").length, color: "text-yellow-700" },
              { label: "Udestående beløb", value: totalOutstanding.toFixed(2) + " kr.", color: totalOutstanding > 0 ? "text-red-600" : "text-green-600" },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 mb-1">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {/* Filter + refresh */}
          <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {([
                { id: "all" as const, label: "Alle" },
                { id: "not_paid" as const, label: "Ubetalt" },
                { id: "paid" as const, label: "Betalt" },
                { id: "draft" as const, label: "Kladde" },
              ]).map((f) => (
                <button
                  key={f.id}
                  onClick={() => setInvoiceFilter(f.id)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                    invoiceFilter === f.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <button
              onClick={loadInvoices}
              disabled={invoicesLoading}
              className="ml-auto text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40"
            >
              🔄 Opdater
            </button>
          </div>

          {/* Pagination */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <button
              onClick={() => setInvoiceOffset(Math.max(0, invoiceOffset - invoiceLimit))}
              disabled={invoiceOffset === 0 || invoicesLoading}
              className="px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 text-xs"
            >
              ← Forrige
            </button>
            <span className="text-xs">Side {Math.floor(invoiceOffset / invoiceLimit) + 1}</span>
            <button
              onClick={() => setInvoiceOffset(invoiceOffset + invoiceLimit)}
              disabled={invoices.length < invoiceLimit || invoicesLoading}
              className="px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 text-xs"
            >
              Næste →
            </button>
          </div>

          {invoicesLoading ? (
            <div className="text-center py-12 text-gray-400">Henter fakturaer...</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200 text-gray-400">
              <div className="text-4xl mb-2">🧾</div>
              <p>Ingen fakturaer fundet</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Fakturanr.</th>
                    <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Kunde</th>
                    <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Dato</th>
                    <th className="text-left px-4 py-2.5 text-xs text-gray-500 font-medium">Forfald</th>
                    <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Total</th>
                    <th className="text-right px-4 py-2.5 text-xs text-gray-500 font-medium">Resterende</th>
                    <th className="px-4 py-2.5 text-xs text-gray-500 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((inv, i) => (
                    <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50 ${inv.state === "draft" ? "opacity-70" : ""}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-700">
                        {inv.name}
                        {inv.state === "draft" && (
                          <span className="ml-1 text-xs text-gray-400">(kladde)</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600 max-w-32 truncate">
                        {Array.isArray(inv.partner_id) ? inv.partner_id[1] : "–"}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{inv.invoice_date || "–"}</td>
                      <td className={`px-4 py-2.5 text-xs ${
                        inv.invoice_date_due && new Date(inv.invoice_date_due) < new Date() && inv.payment_state === "not_paid"
                          ? "text-red-600 font-medium"
                          : "text-gray-500"
                      }`}>
                        {inv.invoice_date_due || "–"}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                        {inv.amount_total?.toFixed(2)} kr.
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-500 text-xs">
                        {inv.amount_residual > 0 ? inv.amount_residual.toFixed(2) + " kr." : "–"}
                      </td>
                      <td className="px-4 py-2.5">{paymentBadge(inv.payment_state)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Sync tab ── */}
      {tab === "sync" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-2">Massesynkronisering til Odoo</h3>
            <p className="text-sm text-gray-500 mb-4">
              Opretter eller opdaterer Odoo <code className="bg-gray-100 px-1 rounded text-xs">res.partner</code> for alle
              lokale brugere der endnu ikke er koblet til Odoo. Allerede synkroniserede brugere springes over.
            </p>
            <button
              onClick={syncAll}
              disabled={syncing}
              className="px-6 py-2.5 text-white rounded-xl text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#2d6a4f" }}
            >
              {syncing ? "⏳ Synkroniserer..." : "🔄 Synkroniser alle brugere → Odoo"}
            </button>
            {syncMsg && (
              <p className="mt-3 text-sm font-medium text-gray-700">{syncMsg}</p>
            )}
          </div>

          {syncResults && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center gap-3">
                <h4 className="font-medium text-gray-800 text-sm">Synkroniseringsresultat</h4>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  {syncResults.filter((r) => r.status === "synced").length} synkroniseret
                </span>
                {syncResults.some((r) => r.status === "error") && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    {syncResults.filter((r) => r.status === "error").length} fejl
                  </span>
                )}
              </div>
              {syncResults.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-400 text-center">Alle brugere er allerede synkroniseret</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs text-gray-500">Bruger</th>
                      <th className="text-left px-4 py-2 text-xs text-gray-500">Status</th>
                      <th className="text-left px-4 py-2 text-xs text-gray-500">Detalje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncResults.map((r, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="px-4 py-2 font-medium text-gray-800">{r.username}</td>
                        <td className="px-4 py-2">
                          {r.status === "synced" ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">✅ Synkroniseret</span>
                          ) : (
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">❌ Fejl</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500">
                          {r.status === "synced" ? `Odoo partner #${r.odoo_partner_id}` : r.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
